import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import {
  buildConversationSelectionIdsKey,
  buildConversationSelectionKey,
  CONVERSATION_IDS_FILTER_ID,
  CONVERSATION_SELECTION_QUERY_PARAM,
  type ConversationSelection,
  getConversationSelectionIds,
  isConversationSelection,
  normalizeConversationSelectionSource,
  takeConversationSelection,
} from '@/entities/conversations';
import { type TableSelectionDescription, useTablePresets } from '@/entities/settings';
import { DEFAULT_DIALOG_SORTING } from './constants';
import { withSelectionColumns } from './selectionColumns';

type Params = {
  /** Ключ рабочей записи раздела в `settings.tables` — вид, поверх которого открывается выборка */
  sectionKey: string;
  /** Личные фильтры таблицы (рабочая запись) */
  filters: TableFilterState;
  /** Личная сортировка таблицы (рабочая запись) */
  sorting: TableSortingState;
  /** Ключ выборки, для которой подсказка уже отклонена */
  dismissedSelectionKey: string | undefined;
  onDismissedSelectionKeyChange: (key: string | null) => void;
  /** Сохранённое описание активной выборки (привязано к её ключу) */
  selectionDescription: TableSelectionDescription | undefined;
  onSelectionDescriptionChange: (value: TableSelectionDescription | null) => void;
};

/**
 * Выборка без состава диалогов: ею выборка названа и в описании отбора, и в автоимени пресета,
 * а сам состав для этого не нужен.
 */
export type AppliedSelection = Omit<ConversationSelection, 'conversationIds'>;

type Result = {
  /** Фильтры таблицы с уже применённой выборкой перехода — их следует показывать и отправлять на бэк */
  filters: TableFilterState;
  /** Сортировка таблицы с учётом перехода (на свежем переходе — умолчание списка) */
  sorting: TableSortingState;
  /** Ключ действующей выборки среди фильтров; `null` — выборки нет */
  activeSelectionKey: string | null;
  /** Действующая выборка — ею она называется вместо номеров диалогов; `null` — неизвестна */
  activeSelection: AppliedSelection | null;
  /**
   * Описание действующей выборки, сверенное с её составом в фильтрах, — его уносит пресет, сохранённый
   * из этого вида; `null` — выборки в виде нет или её описание неизвестно
   */
  activeSelectionDescription: TableSelectionDescription | null;
  /** Выборка, которую стоит предложить сохранить; `null` — подсказка не нужна */
  unsavedSelection: AppliedSelection | null;
  /** Пользователь скрыл подсказку, не сохраняя выборку */
  onDismiss: () => void;
};

/**
 * Принимает внешнюю выборку диалогов и открывает её отдельным слоем
 * вида поверх личных настроек таблицы: применяет только саму выборку и сортировку по умолчанию
 * (по идентификатору диалога по убыванию), оставляя колонки пользователя; личные фильтры и
 * сортировка не применяются, а откладываются в `previousView` для возврата при снятии слоя.
 * Колонки, по которым посчитан элемент дашборда, слой показывает в дополнение к колонкам
 * пользователя — они отложенный вид не меняют и уходят вместе со снятием слоя. Колонки пользователя —
 * это вид, к которому вернёт снятие выборки, а не текущий вид слоя: иначе колонки прежней выборки
 * оставались бы в списке после перехода с соседнего элемента.
 *
 * Выборка живёт среди фильтров таблицы (фильтр по идентификаторам диалогов) — так она попадает
 * в снимок пресета при сохранении. Пользователю поле фильтра не предлагается.
 *
 * Каналов доставки два: состояние навигации (переход в текущей вкладке) и хранилище браузера
 * по ключу в адресе (переход в новую вкладку). Дальше приёма канал ни на что не влияет —
 * распознанная выборка идёт общим путём.
 *
 * Переход одноразовый: принятая выборка снимается с канала доставки. Иначе она возвращалась бы
 * в отбор уже после того, как пользователь её снял, — состояние навигации браузер восстанавливает
 * и при перезагрузке, и при любом возврате к адресу, а выборка из хранилища живёт в памяти
 * страницы до конца её жизни и легла бы заново при первой же навигации внутри раздела.
 *
 * Каждая принятая выборка — новый вход в слой: фильтр, описание и колонки ставятся заново, даже
 * когда состав совпал с уже открытой выборкой. Выборку опознаёт состав вместе с описанием: у соседних
 * блоков среднего балла критериев состав один и тот же, различаются они тем, откуда открыты.
 *
 * «Сохранена ли выборка» выводится не из нажатия кнопки в этом сеансе жизни страницы, а из самих
 * пресетов пользователя: выборка сохранена, если есть пресет с тем же составом диалогов и тем же
 * описанием выборки. Отклонение подсказки хранится рядом с настройками таблицы и привязано к ключу
 * выборки — переход с другого элемента приносит другую выборку и показывает подсказку заново.
 * Пресет и отклонение, оставшиеся с поры, когда выборку различал один состав, описания не несут —
 * их выборка, как и прежде, опознаётся составом: иначе она снова предлагала бы сохранить себя дублем.
 *
 * Описание выборки приходит только вместе с самим переходом, но уход в карточку диалога заменяет
 * состояние навигации (лейаут «таблица + карточка» выбирает диалог сразу), и подсказка пропадала бы.
 * Поэтому при применении выборки описание дублируется в настройки таблицы и берётся оттуда,
 * пока состав выборки в фильтрах не сменился. Пресет, сохранённый из выборки, уносит описание с собой —
 * только сверенное с выборкой сохраняемого вида: в настройках таблицы описание последнего перехода
 * остаётся и после того, как вид сменился.
 */
export const useExternalSelection = ({
  sectionKey,
  filters,
  sorting,
  dismissedSelectionKey,
  onDismissedSelectionKeyChange,
  selectionDescription,
  onSelectionDescriptionChange,
}: Params): Result => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { presets, enterView, selectionBaseView } = useTablePresets(sectionKey);

  // Выборка, открытая новой вкладкой: в адресе лежит только ключ к ней. Забираем её при
  // открытии страницы — хранилище отдаёт выборку и сразу снимает, чтобы повторный заход по
  // тому же адресу не открыл устаревший состав.
  const [stashedSelection, setStashedSelection] = useState<ConversationSelection | null>(() => {
    const stashKey = searchParams.get(CONVERSATION_SELECTION_QUERY_PARAM);
    return stashKey ? takeConversationSelection(stashKey) : null;
  });

  // Выборка перехода в текущей вкладке приходит состоянием навигации
  const navigationSelection = isConversationSelection(location.state) ? location.state : null;
  const selection = navigationSelection ?? stashedSelection;

  const selectionFilter = useMemo<TableFilterState[number] | null>(
    () => (selection ? { id: CONVERSATION_IDS_FILTER_ID, value: selection.conversationIds } : null),
    [selection],
  );

  // Выборку и сортировку по умолчанию показываем сразу в рендере, а не в эффекте после него: список
  // данных строится по фильтрам/сортировке уже на первом рендере, и запоздалая их смена приводила к
  // отмене начального запроса без повторного — список залипал в загрузке до перезагрузки страницы.
  const effectiveFilters = useMemo<TableFilterState>(
    () => (selectionFilter ? [selectionFilter] : filters),
    [selectionFilter, filters],
  );

  const effectiveSorting = useMemo<TableSortingState>(
    () => (selection ? DEFAULT_DIALOG_SORTING : sorting),
    [selection, sorting],
  );

  // Колонки вида, поверх которого открывается выборка: колонки выборки ложатся поверх них. Через ref,
  // а не зависимостью эффекта: смена этого вида вход в слой повторять не должна.
  const baseColumnVisibilityRef = useRef(selectionBaseView.columnVisibility);
  baseColumnVisibilityRef.current = selectionBaseView.columnVisibility;

  // Выборка, уже снятая с адреса: повторное снятие того же состояния — лишний переход
  const clearedNavigationSelectionRef = useRef<ConversationSelection | null>(null);

  // Вход в слой выборки: откладываем личный вид, ставим только выборку и сортировку по умолчанию,
  // колонки пользователя оставляем, добавляя к ним колонки элемента дашборда. Там же дублируем
  // описание — оно переживёт уход в карточку диалога и перезагрузку страницы.
  //
  // Входим на каждой принятой выборке, в том числе поверх уже открытой с тем же составом: это переход
  // с другого элемента, и описание с колонками у него свои. Личный вид откладывается только при входе
  // из него самого, поэтому повторный вход по той же выборке ничего не затирает, а колонки ложатся
  // на вид под слоем — колонки прежней выборки с ним уходят.
  //
  // Дальше выборка снимается с канала доставки — переход одноразовый: иначе он остался бы в истории
  // и лёг бы в отбор снова после того, как пользователь выборку снял.
  useEffect(() => {
    if (!selection || !selectionFilter) return;
    enterView(
      {
        filters: [selectionFilter],
        sorting: DEFAULT_DIALOG_SORTING,
        ...withSelectionColumns(baseColumnVisibilityRef.current, selection.columns),
      },
      { backup: true },
    );
    onSelectionDescriptionChange({
      key: buildConversationSelectionKey(selection),
      text: selection.description,
      source: selection.source,
    });
    setStashedSelection(null);
    if (navigationSelection && clearedNavigationSelectionRef.current !== navigationSelection) {
      clearedNavigationSelectionRef.current = navigationSelection;
      // Адрес остаётся прежним — меняется только состояние текущей записи истории, поэтому
      // сюда идёт не типизированный переход по маршруту, а замена текущего адреса им же:
      // раздел (диалоги или закреплённый пресет) и открытая запись сохраняются как есть.
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: null },
      );
    }
  }, [
    selection,
    selectionFilter,
    navigationSelection,
    enterView,
    onSelectionDescriptionChange,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  // Действующая выборка — та, что лежит в фильтрах таблицы; снятая фильтром больше не существует.
  // Описание и источник берём с самого перехода с дашборда либо из настроек таблицы (после ухода
  // в карточку диалога/перезагрузки) — и только описание действующей выборки: иначе фильтр диалогов
  // уже сменился и описание к нему не относится. Выборка без известного описания (например, из
  // применённого пресета) опознаётся одним составом и сохранить себя не предлагает.
  const { activeSelectionKey, activeSelectionIdsKey, selectionInfo } = useMemo<{
    activeSelectionKey: string | null;
    /**
     * Ключ той же выборки одним составом: им опознаются пресет и отклонение, сохранённые до того, как
     * выборку стали различать описанием
     */
    activeSelectionIdsKey: string | null;
    selectionInfo: AppliedSelection | null;
  }>(() => {
    const conversationIds = getConversationSelectionIds(effectiveFilters);
    if (!conversationIds) return { activeSelectionKey: null, activeSelectionIdsKey: null, selectionInfo: null };
    const activeSelectionIdsKey = buildConversationSelectionIdsKey(conversationIds);
    if (selection) {
      return {
        activeSelectionKey: buildConversationSelectionKey(selection),
        activeSelectionIdsKey,
        selectionInfo: { description: selection.description, source: selection.source },
      };
    }
    if (selectionDescription) {
      const describedSelectionKey = buildConversationSelectionKey({
        conversationIds,
        description: selectionDescription.text,
      });
      // Описание, записанное до того, как выборку стали различать описанием, привязано к одному составу
      if (selectionDescription.key === describedSelectionKey || selectionDescription.key === activeSelectionIdsKey) {
        return {
          activeSelectionKey: describedSelectionKey,
          activeSelectionIdsKey,
          selectionInfo: {
            description: selectionDescription.text,
            source: normalizeConversationSelectionSource(selectionDescription.source),
          },
        };
      }
    }
    return {
      activeSelectionKey: buildConversationSelectionKey({ conversationIds, description: '' }),
      activeSelectionIdsKey,
      selectionInfo: null,
    };
  }, [selection, effectiveFilters, selectionDescription]);

  // Пресет хранит выборку снимком фильтров, а её описание — рядом, с момента сохранения. Пресет без
  // описания сохранён до того, как выборку стали различать описанием, и опознаётся одним составом
  const isSaved = useMemo(
    () =>
      presets.some((preset) => {
        const conversationIds = getConversationSelectionIds(preset.settings.filters ?? []);
        if (conversationIds === null) return false;
        const description = preset.settings.selectionDescription?.text;
        if (description === undefined) {
          return buildConversationSelectionIdsKey(conversationIds) === activeSelectionIdsKey;
        }
        return buildConversationSelectionKey({ conversationIds, description }) === activeSelectionKey;
      }),
    [presets, activeSelectionKey, activeSelectionIdsKey],
  );

  // Отклонение, сохранённое до того, как выборку стали различать описанием, — один состав без описания
  const isDismissed =
    dismissedSelectionKey !== undefined &&
    (dismissedSelectionKey === activeSelectionKey || dismissedSelectionKey === activeSelectionIdsKey);

  const onDismiss = useCallback(() => {
    if (!activeSelectionKey) return;
    onDismissedSelectionKeyChange(activeSelectionKey);
  }, [activeSelectionKey, onDismissedSelectionKeyChange]);

  const isHintNeeded = activeSelectionKey !== null && !isSaved && !isDismissed;

  const activeSelectionDescription = useMemo<TableSelectionDescription | null>(
    () =>
      activeSelectionKey && selectionInfo
        ? { key: activeSelectionKey, text: selectionInfo.description, source: selectionInfo.source }
        : null,
    [activeSelectionKey, selectionInfo],
  );

  return {
    filters: effectiveFilters,
    sorting: effectiveSorting,
    activeSelectionKey,
    activeSelection: selectionInfo,
    activeSelectionDescription,
    unsavedSelection: isHintNeeded ? selectionInfo : null,
    onDismiss,
  };
};
